"use client";

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Info, Mic2, Pencil, Plus, RotateCcw, X } from "lucide-react";
import type { Agent } from "@/lib/api";
import { DEFAULT_AGENT_PROMPTS } from "@/lib/api";
import { AgentPromptEditDialog } from "@/components/agent-prompt-edit-dialog";
import { useCartesiaAudioPlayer, VoicePlayButton } from "@/components/voice-play-button";
import { clampTtsSpeed, clampTtsVolume } from "@/lib/voice-catalog";

const CLASSIFIER_MAX = 2000;
const NUDGE_MAX = 200;
const LIST_PREVIEW = 4;

type EotStage = "brief" | "tails" | "classifier";

function parseList(raw: string | undefined | null): string[] {
  return String(raw || "")
    .split(/\n+/)
    .flatMap((line) => line.split(","))
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinList(items: string[]): string {
  return items.join("\n");
}

function PhraseListCard({
  title,
  blurb,
  items,
  onChange,
}: {
  title: string;
  blurb: string;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const visible = expanded ? items : items.slice(0, LIST_PREVIEW);
  const hidden = Math.max(0, items.length - LIST_PREVIEW);

  function commitAdd() {
    const v = draft.trim();
    if (!v) {
      setAdding(false);
      setDraft("");
      return;
    }
    if (!items.some((x) => x.toLowerCase() === v.toLowerCase())) {
      onChange([...items, v]);
    }
    setDraft("");
    setAdding(false);
  }

  function commitEdit(idx: number) {
    const v = editDraft.trim();
    if (!v) {
      onChange(items.filter((_, i) => i !== idx));
    } else {
      onChange(items.map((x, i) => (i === idx ? v : x)));
    }
    setEditingIdx(null);
    setEditDraft("");
  }

  return (
    <div className="agent-eot-list-card">
      <div className="agent-eot-list-head">
        <div>
          <b>{title}</b>
          <span>
            {items.length} {items.length === 1 ? "entry" : "entries"}
          </span>
        </div>
        <button
          type="button"
          className="agent-eot-add"
          onClick={() => {
            setAdding(true);
            setDraft("");
          }}
        >
          <Plus size={14} aria-hidden />
          Add
        </button>
      </div>
      <p className="agent-eot-list-blurb">{blurb}</p>
      <ul className="agent-eot-list">
        {visible.map((item, idx) => (
          <li key={`${idx}-${item}`}>
            {editingIdx === idx ? (
              <input
                className="agent-conv-input agent-eot-inline-input"
                value={editDraft}
                autoFocus
                onChange={(e) => setEditDraft(e.target.value)}
                onBlur={() => commitEdit(idx)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitEdit(idx);
                  if (e.key === "Escape") {
                    setEditingIdx(null);
                    setEditDraft("");
                  }
                }}
              />
            ) : (
              <>
                <button
                  type="button"
                  className="agent-eot-phrase"
                  onClick={() => {
                    setEditingIdx(idx);
                    setEditDraft(item);
                  }}
                >
                  {item}
                </button>
                <button
                  type="button"
                  className="agent-eot-remove"
                  aria-label={`Remove ${item}`}
                  onClick={() => onChange(items.filter((_, i) => i !== idx))}
                >
                  <X size={14} />
                </button>
              </>
            )}
          </li>
        ))}
        {adding && (
          <li>
            <input
              className="agent-conv-input agent-eot-inline-input"
              value={draft}
              autoFocus
              placeholder="New phrase…"
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => commitAdd()}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitAdd();
                if (e.key === "Escape") {
                  setAdding(false);
                  setDraft("");
                }
              }}
            />
          </li>
        )}
      </ul>
      {!expanded && hidden > 0 && (
        <button type="button" className="agent-eot-more" onClick={() => setExpanded(true)}>
          + {hidden} more {hidden === 1 ? "entry" : "entries"}
        </button>
      )}
      {expanded && items.length > LIST_PREVIEW && (
        <button type="button" className="agent-eot-more" onClick={() => setExpanded(false)}>
          Show less
        </button>
      )}
    </div>
  );
}

const STAGES: Array<{
  id: EotStage;
  n: string;
  title: string;
  blurb: string;
}> = [
  {
    id: "brief",
    n: "1",
    title: "Brief complete",
    blurb: "User finished an intent. Exact phrase → reply now.",
  },
  {
    id: "tails",
    n: "2",
    title: "Incomplete tails",
    blurb: "User likely still speaking. Last-word fallback.",
  },
  {
    id: "classifier",
    n: "3",
    title: "AUX classifier",
    blurb: "Semantic YES/NO decision. Main tuning point.",
  },
];

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
  /** Workspace default voice when the agent has none selected. */
  workspaceVoiceId?: string | null;
  workspaceVoiceName?: string | null;
};

export function AgentEditEndpointingPanel({
  draft,
  onChange,
  workspaceVoiceId = null,
  workspaceVoiceName = null,
}: Props) {
  const [learnOpen, setLearnOpen] = useState(false);
  const [stage, setStage] = useState<EotStage>("classifier");
  const [promptOpen, setPromptOpen] = useState(false);
  const nudgePlayer = useCartesiaAudioPlayer();
  const nudgePlayKey = "nudge:soft";

  const brief = useMemo(() => parseList(draft.briefComplete), [draft.briefComplete]);
  const tails = useMemo(() => parseList(draft.incompleteTails), [draft.incompleteTails]);
  const classifier = draft.semanticPrompt || "";
  const nudge = draft.semanticNudgeText || "";
  const effectiveVoiceId = (draft.voiceId || workspaceVoiceId || "").trim() || null;
  const effectiveVoiceLabel = draft.voiceId
    ? (draft.voiceName || draft.voiceId)
    : workspaceVoiceId
      ? (workspaceVoiceName || workspaceVoiceId)
      : null;
  const canPreviewNudge = Boolean(effectiveVoiceId);

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  function playNudge() {
    const text = (nudge || "Yes?").trim();
    if (!text || !effectiveVoiceId) return;
    void nudgePlayer.playSynthesized(nudgePlayKey, {
      transcript: text,
      voiceId: effectiveVoiceId,
      speed: clampTtsSpeed(draft.ttsSpeed, 1),
      volume: clampTtsVolume(draft.ttsVolume, 1),
      emotion: draft.ttsEmotion,
    });
  }

  const classifierPreview = classifier.trim()
    || DEFAULT_AGENT_PROMPTS.semanticPrompt;
  const nudgePlayState = nudgePlayer.stateFor(nudgePlayKey);

  return (
    <div className="agent-conv agent-eot">
      <section className="agent-conv-block">
        <div className="agent-eot-title-row">
          <div>
            <h3 className="agent-conv-h">End of turn</h3>
            <p className="agent-conv-guards-lead">
              Control how the agent detects user completion and decides when to reply.
            </p>
          </div>
          <button
            type="button"
            className="agent-guards-tips-link"
            onClick={() => setLearnOpen((o) => !o)}
          >
            Learn more →
          </button>
        </div>

        {learnOpen && (
          <div className="agent-guards-tips" role="note">
            <p>
              <b>End of turn (EOT)</b> is how the agent decides the user has finished speaking
              and it is safe to reply. Checks run in a fixed order — first match wins:
            </p>
            <ol className="agent-eot-learn-list">
              <li>
                <b>Brief complete</b> — if the transcript exactly matches a short “I&apos;m done”
                phrase, reply immediately (skip the rest).
              </li>
              <li>
                <b>Incomplete tails</b> — if the last word looks like mid-sentence (e.g. “and”,
                “but”), keep listening a bit longer.
              </li>
              <li>
                <b>AUX classifier</b> — otherwise a small model answers YES (done) or NO (still
                speaking). This is the main place to fix mid-sentence barge-ins — prefer tuning
                it over growing the tails list.
              </li>
            </ol>
            <p>
              Click a step above to edit that stage. Soft nudge (under the classifier) is a short
              line the agent can say when it is unsure the user is finished.
            </p>
          </div>
        )}

        <div
          className="agent-eot-flow"
          role="tablist"
          aria-label="End-of-turn pipeline"
        >
          {STAGES.flatMap((s, i) => {
            const tab = (
              <button
                key={s.id}
                type="button"
                role="tab"
                id={`agent-eot-tab-${s.id}`}
                aria-selected={stage === s.id}
                aria-controls="agent-eot-stage"
                className={`agent-eot-step${stage === s.id ? " is-selected" : ""}`}
                onClick={() => setStage(s.id)}
              >
                <span>{s.n}</span>
                <b>{s.title}</b>
                <em>{s.blurb}</em>
              </button>
            );
            if (i === 0) return [tab];
            return [
              <div key={`arrow-${s.id}`} className="agent-eot-arrow" aria-hidden>
                →
              </div>,
              tab,
            ];
          })}
        </div>

        <div
          className="agent-eot-stage"
          id="agent-eot-stage"
          role="tabpanel"
          aria-labelledby={`agent-eot-tab-${stage}`}
        >
          {stage === "brief" && (
            <>
              <div className="agent-conv-tip" role="note">
                <span className="agent-conv-tip-icon" aria-hidden>
                  <Info size={14} />
                </span>
                <p>
                  Keep this list short — exact matches only. Prefer the AUX classifier for fuzzy
                  “done talking” cases.
                </p>
              </div>
              <PhraseListCard
                title="Brief complete"
                blurb="Exact phrases that commit a reply immediately (skip AUX)."
                items={brief}
                onChange={(next) => patch({ briefComplete: joinList(next) })}
              />
            </>
          )}

          {stage === "tails" && (
            <>
              <div className="agent-conv-tip" role="note">
                <span className="agent-conv-tip-icon" aria-hidden>
                  <Info size={14} />
                </span>
                <p>
                  Don&apos;t grow Incomplete tails to stop barge-ins. Tune the classifier instead.
                </p>
              </div>
              <PhraseListCard
                title="Incomplete tails"
                blurb="If the last word matches, treat the turn as unfinished."
                items={tails}
                onChange={(next) => patch({ incompleteTails: joinList(next) })}
              />
            </>
          )}

          {stage === "classifier" && (
            <>
              <div className="agent-conv-tip" role="note">
                <span className="agent-conv-tip-icon" aria-hidden>
                  <Info size={14} />
                </span>
                <p>
                  Main tuning lever for mid-sentence barge-ins. The model must answer only YES or
                  NO.
                </p>
              </div>

              <div className="agent-eot-prompt-card" id="agent-eot-classifier">
                <div className="agent-eot-prompt-card-head">
                  <div>
                    <b>Classifier prompt</b>
                    <span>
                      Decides if the user has finished their turn and the agent should reply. Return
                      only YES or NO.
                    </span>
                  </div>
                  <div className="agent-eot-prompt-actions">
                    <button
                      type="button"
                      className="agent-prompt-reset"
                      onClick={() =>
                        patch({ semanticPrompt: DEFAULT_AGENT_PROMPTS.semanticPrompt })
                      }
                    >
                      <RotateCcw size={12} aria-hidden />
                      Reset to default
                    </button>
                    <button
                      type="button"
                      className="agent-edit-btn agent-edit-btn--primary agent-eot-edit-btn"
                      onClick={() => setPromptOpen(true)}
                    >
                      <Pencil size={14} aria-hidden />
                      Edit
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="agent-eot-prompt-preview-btn"
                  onClick={() => setPromptOpen(true)}
                >
                  <pre className="agent-eot-prompt-preview">{classifierPreview}</pre>
                </button>
                <div className="agent-conv-field-foot">
                  <span className="agent-conv-looks-muted">End-of-turn classifier prompt</span>
                  <span
                    className={`agent-conv-count${classifierPreview.length > CLASSIFIER_MAX ? " is-over" : ""}`}
                  >
                    {classifierPreview.length.toLocaleString()} / {CLASSIFIER_MAX.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="agent-eot-nudge">
                <div className="agent-eot-nudge-copy">
                  <b>Soft nudge</b>
                  <span>Short prompt the agent can say when unsure if the user is finished.</span>
                </div>
                <div className="agent-eot-nudge-row">
                  <span className="agent-eot-nudge-mic" aria-hidden>
                    <Mic2 size={16} />
                  </span>
                  <input
                    className="agent-conv-input"
                    value={nudge}
                    maxLength={NUDGE_MAX}
                    placeholder="Yes?"
                    onChange={(e) => patch({ semanticNudgeText: e.target.value })}
                  />
                  <VoicePlayButton
                    variant="nudge"
                    playState={nudgePlayState}
                    disabled={!canPreviewNudge}
                    aria-label={
                      !canPreviewNudge
                        ? "Preview unavailable — select a voice first"
                        : nudgePlayState === "playing"
                          ? "Pause soft nudge preview"
                          : nudgePlayState === "loading"
                            ? "Loading soft nudge preview"
                            : "Preview soft nudge with agent voice"
                    }
                    onClick={() => playNudge()}
                  />
                  <span className={`agent-conv-count${nudge.length > NUDGE_MAX ? " is-over" : ""}`}>
                    {nudge.length} / {NUDGE_MAX}
                  </span>
                </div>
                {nudgePlayer.error && nudgePlayer.activeKey === nudgePlayKey ? (
                  <p className="agent-voice-error" role="alert">{nudgePlayer.error}</p>
                ) : !canPreviewNudge ? (
                  <p className="agent-eot-nudge-warn" role="status">
                    Preview is unavailable until you pick a voice on the Voice tab, or{" "}
                    <Link to="/settings/voice" className="agent-eot-nudge-link">
                      set a workspace default under Settings
                    </Link>
                    .
                  </p>
                ) : (
                  <p className="agent-eot-nudge-hint">
                    Preview uses this agent&apos;s voice and delivery settings
                    {effectiveVoiceLabel ? ` (${effectiveVoiceLabel}).` : "."}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <AgentPromptEditDialog
        open={promptOpen}
        onOpenChange={setPromptOpen}
        title="Edit end-of-turn classifier prompt"
        description="Decide whether the user has finished speaking and the agent can reply now. Respond with only YES or NO."
        banner="This classifier runs in real time and is the main lever for mid-sentence barge-ins."
        value={classifier || DEFAULT_AGENT_PROMPTS.semanticPrompt}
        max={CLASSIFIER_MAX}
        defaultValue={DEFAULT_AGENT_PROMPTS.semanticPrompt}
        emptyHint="If left empty, the platform default classifier prompt is used."
        onSave={(next) => patch({ semanticPrompt: next })}
      />
    </div>
  );
}
